---
title: Scope Configuration
sidebar_position: 5
description: Scope Configuration
---

# Scope Configuration & Customization

## Overview

Each scope in the AM App has customizable settings that determine which features, layers, and defaults are available. Configuration is stored per-scope in the admin service. An in-app **Config Editor** lets administrators with the **SUPER-SYSADMIN** role edit the configuration directly in the app. End users without that role do not see the Config Editor.

#### Why Scope Configuration Matters

- Different agricultural regions have different monitoring needs
- Scopes can be tailored to specific use cases, crops, seasons, or compliance rules
- Configuration determines what you see and how you interact with the data

**Important**: You cannot directly change scope configuration as an end user. Configuration is managed by administrators. However, understanding what's configurable helps you know why scopes behave differently.

## Editing Scope Configuration

The **Config Editor** displays the raw stored JSON for the scope and provides:

- A JSON code editor with syntax highlighting
- Inline validation against the configuration schema — invalid edits block saving, so schema errors are caught before they reach users
- A warning when you have unsaved changes
- A Save button that writes the updated configuration back to the scope immediately

To access it: open the sidebar and select **Config Editor**.

## Configurable Elements per Scope

The following elements can be customized for each scope:

---

### Time Range Defaults

**What It Controls**:

- Default start and end dates when you open a scope
- Typically set to the current growing season or monitoring period

**User Impact**:

- When you first select a scope, the time range is pre-set
- You can override these defaults using the Options drawer
- Defaults ensure you're looking at relevant data

**Example**:

```json
{
  "options": {
    "timerange": {
      "start": "2023-01-01",
      "end": "2023-12-31"
    }
  }
}
```

---

### Map Settings

**What It Controls**:

1. **Available Base Layers** (`map.baseLayers`):
   - Which tile layers are available in the Layers menu
   - Three source types are supported: `WMS`, `WMTS`, and `XYZ` — set via the `type` field on each entry
   - All three types share the common fields `id` (unique identifier) and `label` (display name shown in the Layers menu)
   - Type-specific fields:
     - **WMS**: `params.LAYERS` (required — layer name string or array); optionally `url` (single endpoint) or `urls` (array of endpoints), `serverType`, `projection`, `tileGrid`
     - **WMTS**: `layer`, `style`, `matrixSet`, `tileGrid` (with `origin`, `tileSize`, `resolutions`, `matrixIds`); optionally `url`, `format`, `projection`
     - **XYZ**: optionally `url` (single tile-URL template with `{z}`, `{x}`, `{y}` placeholders) or `urls` (array of templates), `maxZoom`, `minZoom`, `projection`
   - All types accept optional `attributions` (string shown in the map attribution control)

2. **FOI Zoom Threshold** (`map.foiZoomThreshold`):
   - Zoom level at which FOIs become visible on the map
   - Prevents clutter when zoomed out
   - Typical value: zoom level 12–14

3. **Label Zoom Threshold per FOI Type** (`map.labelZoomThresholdPerFoiType`):
   - Per-FOI-type zoom level at which FOI labels appear on the map
   - Allows fine-grained label control when a scope has multiple FOI types at different densities

4. **Measurement Unit System** (`map.measurement`):
   - Controls **area units only**: Metric (m²/km² auto-scaled, or hectares) or Imperial (acres)
   - Distance is always displayed in metres or kilometres regardless of this setting

**User Impact**:

- Different scopes offer different base layers
- Some scopes may use imperial area units (e.g., US projects)
- FOI visibility and label appearance change based on zoom level

**Example**:

```json
{
  "map": {
    "baseLayers": [
      {
        "id": "world_imagery",
        "label": "World Imagery",
        "type": "XYZ",
        "url": "https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        "attributions": "Tiles © ArcGIS",
        "maxZoom": 19
      },
      {
        "id": "my_wms_layer",
        "label": "National Orthophoto (WMS)",
        "type": "WMS",
        "url": "https://example.com/ows",
        "params": {
          "LAYERS": "orthophoto_2023",
          "VERSION": "1.3.0"
        },
        "attributions": "© National Mapping Agency"
      },
      {
        "id": "ORTHOIMAGERY.ORTHOPHOTOS.BDORTHO",
        "url": "https://data.geopf.fr/wmts",
        "type": "WMTS",
        "label": "Aerial imagery 50 cm",
        "layer": "ORTHOIMAGERY.ORTHOPHOTOS.BDORTHO",
        "style": "normal",
        "format": "image/jpeg",
        "tileGrid": {
          "origin": [-20037508.342789244, 20037508.342789244],
          "tileSize": 256,
          "matrixIds": [
            "0",
            "1",
            "2",
            "3",
            "4",
            "5",
            "6",
            "7",
            "8",
            "9",
            "10",
            "11",
            "12",
            "13",
            "14",
            "15",
            "16",
            "17",
            "18"
          ],
          "resolutions": [
            156543.03392804097, 78271.51696402048, 39135.75848201024, 19567.87924100512, 9783.93962050256,
            4891.96981025128, 2445.98490512564, 1222.99245256282, 611.49622628141, 305.748113140705,
            152.8740565703525, 76.43702828517625, 38.21851414258813, 19.109257071294063, 9.554628535647032,
            4.777314267823516, 2.388657133911758, 1.194328566955879, 0.5971642834779395
          ]
        },
        "matrixSet": "PM_6_18",
        "projection": "EPSG:3857",
        "attributions": "© IGN"
      }
    ],
    "foiZoomThreshold": 14,
    "labelZoomThresholdPerFoiType": {
      "PARCEL": 13,
      "HOLDING": 10
    },
    "measurement": {
      "system": "metric",
      "areaUnit": "sqm-km2-auto"
    }
  }
}
```

---

### Map Projections

**What It Controls**:

Custom coordinate reference systems (CRS) that the map supports for this scope. By default the app uses Web Mercator (EPSG:3857). When a scope's data is held in a national projection — such as Czech S-JTSK (EPSG:5514) or French Lambert-93 (EPSG:2154) — the corresponding CRS must be registered here so that geometries are displayed and measured correctly.

Each entry in the `projections` array has:

| Field        | Required | Description                                                            |
| ------------ | -------- | ---------------------------------------------------------------------- |
| `crs`        | Yes      | CRS identifier, e.g. `"EPSG:5514"`                                     |
| `definition` | Yes      | Proj4 definition string for that CRS                                   |
| `extent`     | No       | Bounding box in the CRS's native units: `minX`, `minY`, `maxX`, `maxY` |

**User Impact**:

- Enables correct rendering of FOI geometries that are stored in a national projection
- Enables accurate area and distance measurements in the scope's native units
- Without the matching projection registered, geometries may appear distorted or shifted on the map

**Example**:

```json
{
  "projections": [
    {
      "crs": "EPSG:5514",
      "definition": "+proj=krovak +lat_0=49.5 +lon_0=24.8333333333333 +alpha=30.2881397527778 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=589,76,480,0,0,0,0 +units=m +no_defs +type=crs",
      "extent": {
        "minX": -951499.37,
        "minY": -1250000.0,
        "maxX": -159365.31,
        "maxY": -915744.49
      }
    }
  ]
}
```

---

### Default Map Layer Settings per FOI Type

**What It Controls**:

Each FOI type can have its own default map display settings via `map.layerOptions`. The key is the FOI type code; each entry controls:

| Property           | Type                                  | Description                                            |
| ------------------ | ------------------------------------- | ------------------------------------------------------ |
| `visible`          | boolean                               | Whether this FOI type is visible on the map by default |
| `border`           | boolean                               | Whether FOI borders are drawn                          |
| `fill`             | boolean                               | Whether FOI polygons are filled                        |
| `opacity`          | number (0–100)                        | Fill opacity percentage                                |
| `labels`           | string array                          | Attribute keys to show as map labels                   |
| `colorBy`          | `fixedColor` / `attribute` / `marker` | How FOI polygons are coloured                          |
| `fixedColor`       | string (nullable)                     | Hex colour used when `colorBy` is `fixedColor`         |
| `colorByAttribute` | string (nullable)                     | Attribute key used when `colorBy` is `attribute`       |
| `colorByMarker`    | string (nullable)                     | Marker type code used when `colorBy` is `marker`       |

**User Impact**:

- When you open a scope, FOI types appear on the map with the colours, fills, and labels set by the administrator
- You can override these defaults in the FOI visualization options panel — your personal settings take precedence
- If a scope has multiple FOI types, each can have a distinct visual style by default

**Example**:

```json
{
  "map": {
    "layerOptions": {
      "PARCEL": {
        "visible": true,
        "border": true,
        "fill": true,
        "opacity": 60,
        "labels": ["ref_id"],
        "colorBy": "marker",
        "colorByMarker": "ARPS_HOMOGENEITY_MARKER"
      },
      "HOLDING": {
        "visible": true,
        "border": true,
        "fill": false,
        "colorBy": "fixedColor",
        "fixedColor": "#FFD700"
      }
    }
  }
}
```

---

### Sentinel Hub Integration

**What It Controls**:

- **Instance ID**: Identifier for accessing Sentinel Hub layers
- **Available Observation Layers**: Which visualizations are offered
  - Examples: NDVI, True Color, False Color, EVI, LAI, etc.
  - Each scope can have a unique set of layers defined in Planet Insights Platform Configurations

**User Impact**:

- The observation layers you see in the Options drawer vary by scope
- Some scopes have many layers; others have just a few
- Layer availability depends on project needs and Sentinel Hub configuration

**Example**:

```json
{
  "sentinelHub": {
    "shInstanceId": "81b306fe-0435-44bd-9f1f-d516999a8e97"
  }
}
```

---

### Signals Configuration

For each signal type, the scope configuration can define:

**What It Controls**:

1. **Which Signal Types Are Available**: Scope determines which signals are calculated
2. **Default Visibility in Signal/Marker Visualization**: Whether the signal is shown by default
3. **Default Tooltip Visibility**: Whether the signal appears in tooltips by default
4. **Color Schemes**: Light mode and dark mode colors for each signal (single color string, or array of colors for gradient signals)
5. **Valid Signal Type Overrides** (`options.validSignalTypeOverrides`): remaps which signal type is used to decide whether an observation is valid. Each entry is keyed by a signal type code; the value is the replacement signal type code to use instead, or `null` to suppress validity filtering for that type.

**User Impact**:

- Your Signal/Marker Visualization widget will show signals defined for your scope
- Some signals may be hidden by default (toggle them on in the legend)
- Signal colors are consistent within a scope

**Example**:

```json
{
  "signals": {
    "DEMO_FR23_S2_NDVI": {
      "sigMaVisShow": true,
      "sigMaVisTooltip": true,
      "color": {
        "dark": "#000000",
        "light": "#000000"
      }
    }
  }
}
```

Valid-signal override example (use `S2_NDVI` validity for `PF_NDVI`, and disable validity filtering for `S2_EVI`):

```json
{
  "options": {
    "validSignalTypeOverrides": {
      "PF_NDVI": "S2_NDVI",
      "S2_EVI": null
    }
  }
}
```

---

### Markers Configuration

For each marker type, the scope configuration can define:

**What It Controls**:

1. **Which Marker Types Are Available**: Scope determines which markers are calculated
2. **Default Visibility in Signal/Marker Visualization**: Shown or hidden by default
3. **Default Tooltip Visibility**: In tooltips by default or not
4. **Color Schemes**: Light and dark mode colors (single color string or array for gradients)
5. **Attribute-Based Coloring Schemes for Map Visualization** (`layerColourScheme`):
   - Defines value-to-color mappings
   - Example: Traffic light colors (Green, Yellow, Red)
   - Used when coloring FOIs by marker results

**User Impact**:

- Available markers vary by scope
- Map FOI coloring options depend on marker configuration
- Marker colors in visualizations match scope settings

**Example**:

```json
{
  "markers": {
    "ARPS_HOMOGENEITY_MARKER": {
      "sigMaVisShow": false,
      "sigMaVisTooltip": false,
      "color": {
        "dark": "#000000",
        "light": "#000000"
      },
      "layerColourScheme": [
        {
          "value": "HOMOGENEOUS",
          "colour": "#009688"
        },
        {
          "value": "HETEROGENEOUS",
          "colour": "#FFC107"
        }
      ]
    }
  }
}
```

---

### FOI Attribute Display

**What It Controls**:

The `foiAttributes` key controls which additional FOI attributes are surfaced in the app and how they appear. It is a record keyed by FOI type code (e.g. `"PARCEL"`), where each value is an array of attribute display entries.

Each entry in the array has:

| Field                  | Required              | Description                                                                       |
| ---------------------- | --------------------- | --------------------------------------------------------------------------------- |
| `attributeKey`         | Yes                   | The attribute identifier to configure                                             |
| `preview`              | No                    | Controls visibility in the map **Preview Details** (Selection Preview) panel      |
| `preview.collapsed`    | No                    | `true` to show this attribute in the panel's collapsed (summary) state            |
| `preview.expanded`     | No                    | `true` to show this attribute when the panel is expanded                          |
| `sigMaVis`             | No                    | Renders the attribute as a static reference marker on the Signals & Markers chart |
| `sigMaVis.label`       | Yes (if sigMaVis set) | Display label for the reference line                                              |
| `sigMaVis.show`        | Yes (if sigMaVis set) | Whether the reference line is shown by default                                    |
| `sigMaVis.tooltip`     | Yes (if sigMaVis set) | Whether the reference line appears in chart tooltips                              |
| `sigMaVis.color.light` | Yes (if sigMaVis set) | Hex color for light mode                                                          |
| `sigMaVis.color.dark`  | Yes (if sigMaVis set) | Hex color for dark mode                                                           |

**User Impact**:

- Attributes configured with `preview` appear in the floating **Preview Details** panel that opens when you click an FOI on the map (see [Preview Details Panel](./02-getting-started)). The `collapsed` / `expanded` flags control information density: key identifiers visible at a glance vs. the full set when the panel is expanded.
- Attributes configured with `sigMaVis` appear as horizontal reference lines on the Signals & Markers chart alongside signal and marker time series (see [FOI View Module](./03-foi-view-module)) — useful for showing a static value (e.g. declared area, reference NDVI) directly on the chart.

**Example**:

```json
{
  "foiAttributes": {
    "PARCEL": [
      {
        "attributeKey": "declared_area_ha",
        "preview": {
          "collapsed": true,
          "expanded": true
        },
        "sigMaVis": {
          "label": "Declared area (ha)",
          "show": true,
          "tooltip": true,
          "color": {
            "light": "#007C91",
            "dark": "#00CBE5"
          }
        }
      },
      {
        "attributeKey": "farmer_name",
        "preview": {
          "collapsed": false,
          "expanded": true
        }
      }
    ]
  }
}
```

---

### Multi-Axis Visualization Settings

**What It Controls**:

1. **Enable/Disable Multi-Axis Charts**: Turn multi-axis feature on or off
2. **Up to 3 Axes Configuration**: Define up to 3 Y-axes
3. **Axis Properties**:
   - **Position**: Left or right side of the graph
   - **Domain**: Min and max values for the axis
   - **Labels**: Optional axis labels
   - **Signal Type Assignments**: Which signals are plotted on which axis

**User Impact**:

- If enabled, your Signal/Marker Visualization can show multiple axes
- Signals with different value ranges (e.g., 0-1 for NDVI, 0-900 for CCC) plot on separate axes
- Makes it easier to compare disparate signals

**Constraints**:

- Maximum 3 axes total
- Maximum 2 axes per side (left or right)
- Axis IDs must be unique

**Example**:

```json
{
  "sigMaVisAxes": {
    "enabled": true,
    "axes": [
      {
        "id": "land surface temperature",
        "position": "right",
        "domain": [0, 45],
        "tickCount": 10,
        "signalTypeCodes": ["LST_10M_AVG", "LST_10M_CLIM", "LST_10M_V2_LST_C"]
      }
    ]
  }
}
```

---

### Photo Integration

**What It Controls**:

1. **Enable/Disable Geo-Tagged Photos**: Turn photo display on or off
2. **Customer ID for Photo Service Access**: Identifier for accessing photo data

**User Impact**:

- If enabled, geo-tagged photos appear on the map
- Photos provide ground-truth visual context
- Not all scopes have photo access

**Example**:

```json
{
  "photos": {
    "show": true,
    "customerId": "YOUR_CUSTOMER_ID"
  }
}
```

---

### PDF Report Generation

**What It Controls**:

- Whether the **Reports** tab appears in the Validation Console for this scope
- Enabled by setting `reporting.pdfGeneration.show` to `true`

**User Impact**:

- When enabled, a **Reports** tab appears in the Validation Console left column
- Users can select observations and generate a PDF report directly from the Validation Console
- When disabled (or not set), the Reports tab is not shown

See [Validation Console](./04-validation-console) for how to generate a PDF report once this feature is enabled for your scope.

**Example**:

```json
{
  "reporting": {
    "pdfGeneration": {
      "show": true
    }
  }
}
```

## User Impact Summary

Different scopes may have completely different:

| Aspect                    | Impact Example                                                                                             |
| ------------------------- | ---------------------------------------------------------------------------------------------------------- |
| **Available Layers**      | Scope A has 15 layers; Scope B has 5 layers                                                                |
| **Signal/Marker Types**   | Scope A tracks mowing; Scope B does not                                                                    |
| **Map Behavior**          | Scope A uses metric; Scope B uses imperial                                                                 |
| **Default Time Ranges**   | Scope A: Jan-Dec; Scope B: May-Sep                                                                         |
| **Photo Access**          | Scope A has photos; Scope B does not                                                                       |
| **FOI Map Appearance**    | Scope A colours parcels by homogeneity marker; Scope B uses a fixed colour                                 |
| **PDF Reports**           | Scope A shows a Reports tab; Scope B does not                                                              |
| **Map Projection**        | Scope A uses Web Mercator; Scope B registers Czech S-JTSK for national data                                |
| **FOI Attribute Display** | Scope A shows declared area in the Preview Details panel and as a chart reference line; Scope B shows none |

**Key Takeaway**: When you switch scopes, the application's available features and data change. Always check the scope configuration to understand what's available.
